import { useQuery } from '@apollo/client';
import Button from '@mui/material/Button';
import gql from 'graphql-tag';
import { clone } from 'lodash';
import { isString } from 'lodash';
import { findIndex } from 'lodash';
import uniqueId from 'lodash/uniqueId';
import { forwardRef } from 'react';
import { useImperativeHandle } from 'react';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { useMemo } from 'react';
import React from 'react';
import ItemTable from '../../components/ItemTable';
import useMutationFHG from '../../fhg/components/data/useMutationFHG';
import DataLoadingAndErrors from '../../fhg/components/DataLoadingAndErrors';
import FHGTypography from '../../fhg/components/Typography';
import { removeOne } from '../../fhg/utils/Utils';
import PipeDiameterEdit from './PipeDiameterEdit';

const PIPE_DIAMETER_INFO_FRAGMENT = gql`
  fragment pipeDiameterInfo on PipeDiameter {
    id
    diameter
    operatorId
  }
`;

const PIPE_DIAMETERS_ALL_WHERE_QUERY = gql`
   query getPipeDiametersAllWhere($operatorId: [Int] ) {
      pipeDiameters:pipeDiameter_AllWhere(pipeDiameterSearch: {operatorId: $operatorId}) {
         ...pipeDiameterInfo
      }
   }
   ${PIPE_DIAMETER_INFO_FRAGMENT}
`;

const PIPE_DIAMETER_FRAGMENT = gql`
   fragment pipeDiameterInfo on PipeDiameter {
      id
      diameter
      operatorId
   }
`;

const PIPE_DIAMETER_CREATE = gql`
   mutation pipeDiameterCreate($diameter: Float!, $operatorId: Int!)
   {
      pipeDiameter: pipeDiameter_Create(pipeDiameter: {diameter: $diameter, operatorId: $operatorId}) {
         ...pipeDiameterInfo
      }
   }
   ${PIPE_DIAMETER_FRAGMENT}
`;

const PIPE_DIAMETER_UPDATE = gql`
   mutation pipeDiameterUpdate($id: Int!, $diameter: Float!)
   {
      pipeDiameter: pipeDiameter_Update(pipeDiameterId: $id, pipeDiameter: {diameter: $diameter}) {
         ...pipeDiameterInfo
      }
   }
   ${PIPE_DIAMETER_FRAGMENT}
`;

const PIPE_DIAMETER_DELETE = gql`
   mutation pipeDiameterDelete($id: Int!) {
      pipeDiameter_Delete(pipeDiameterId: $id)
   }
`;

const PipeDiameterTable = forwardRef(
   function PipeDiameterTable({ operatorId, onChange, initialItems }, ref) {
      const [pipeDiameterId, setPipeDiameterId] = useState();
      const [pipeDiameterValue, setPipeDiameter] = useState();
      const [deletedPipeDiameters, setDeletedPipeDiameters] = useState([]);

      const { data, loading, error } = useQuery(PIPE_DIAMETERS_ALL_WHERE_QUERY, { variables: { operatorId: [operatorId] }, skip: !operatorId });

      const [pipeDiameterCreate] = useMutationFHG(PIPE_DIAMETER_CREATE, {
         refetchQueries: () => ([{ query: PIPE_DIAMETERS_ALL_WHERE_QUERY, variables: { operatorId: [operatorId] } }])
      });
      const [pipeDiameterUpdate] = useMutationFHG(PIPE_DIAMETER_UPDATE, {
         refetchQueries: () => ([{ query: PIPE_DIAMETERS_ALL_WHERE_QUERY, variables: { operatorId: [operatorId] } }])
      });

      const [pipeDiameterDelete] = useMutationFHG(PIPE_DIAMETER_DELETE, {
         refetchQueries: () => ([{ query: PIPE_DIAMETERS_ALL_WHERE_QUERY, variables: { operatorId: [operatorId] } }])
      });

      const [pipeDiameters, setPipeDiameters] = useState();

      const [showEdit, setShowEdit] = useState(false);
      const [isChanged, setIsChanged] = useState(false);

      useImperativeHandle(ref, () => ({
         async submit(operatorIdOverride) {
            const effectiveOperatorId = operatorIdOverride ?? operatorId;
            if (isChanged) {
               for (const pipeDiameter of pipeDiameters) {
                  if (isString(pipeDiameter.id)) {
                     // New record - CREATE
                     await pipeDiameterCreate({
                        variables: {
                           diameter: pipeDiameter.diameter,
                           operatorId: effectiveOperatorId
                        }
                     });
                  } else if (pipeDiameter.id > 0) {
                     // Existing record - UPDATE
                     await pipeDiameterUpdate({
                        variables: {
                           id: pipeDiameter.id,
                           diameter: pipeDiameter.diameter
                        }
                     });
                  }
               }
               for (const deletedPipeDiameter of deletedPipeDiameters) {
                  if (deletedPipeDiameter.id > 0) {
                     await pipeDiameterDelete({ variables: { id: deletedPipeDiameter.id } });
                  }
               }
               setDeletedPipeDiameters([]);
               setIsChanged(false);
            }
         },
         refetch() {
            setShowEdit(false);
            setIsChanged(false);
         }
      }), [pipeDiameters, deletedPipeDiameters, isChanged, pipeDiameterCreate, pipeDiameterUpdate, pipeDiameterDelete, operatorId]);

      useEffect(() => {
         if (!operatorId && initialItems) {
            setPipeDiameters(clone(initialItems));
            if (initialItems.length > 0) {
               setIsChanged(true);
            }
         } else if (data?.pipeDiameters) {
            setPipeDiameters(clone(data.pipeDiameters));
         }
      }, [data, operatorId, initialItems]);

      const onEditCompleted = useCallback((updatedValue) => {
         const cache = [...(pipeDiameters || [])];
         const index = findIndex(cache, { id: updatedValue.id });

         if (index < 0) {
            // If the item doesn't exist, add it
            setPipeDiameters([...cache, updatedValue]);
         } else {
            // If the item exists, update it
            const updatedCache = [...cache];
            updatedCache[index] = updatedValue;
            setPipeDiameters(updatedCache);
         }

         setPipeDiameterId(updatedValue?.id);
         setPipeDiameter(updatedValue);
         setShowEdit(false);
         setIsChanged(true);
         onChange && onChange();
      }, [pipeDiameters, onChange]);

      const columns = useMemo(() => ([
         { Header: 'Diameter', accessor: 'diameter' },
      ]), []);

      const handleSelect = (pipeDiameter, isDoubleClick = false) => {
         setPipeDiameter(pipeDiameter);
         setPipeDiameterId(pipeDiameter?.id);
         setShowEdit(isDoubleClick);
      };

      const handleAdd = useCallback(() => {
         const newPipeDiameter = { id: uniqueId('-'), diameter: '', operatorId };
         setPipeDiameter(newPipeDiameter);
         setShowEdit(true);
      }, [operatorId]);

      const handleEdit = useCallback(() => {
         setShowEdit(true);
      }, []);

      const handleDelete = useCallback(() => {
         setIsChanged(true);
         if (!isString(pipeDiameterValue?.id)) {
            const cacheDeleted = clone(deletedPipeDiameters);
            cacheDeleted.push(pipeDiameterValue);
            setDeletedPipeDiameters(cacheDeleted);
         }

         const index = findIndex(pipeDiameters, { id: pipeDiameterValue?.id });

         if (index >= 0) {
            const cache = [...pipeDiameters];
            removeOne(cache, index);
            setPipeDiameters(cache);
            onChange && onChange();
         } else {
            console.log('Could not find pipe diameter to delete', pipeDiameterValue);
         }
      }, [pipeDiameters, pipeDiameterValue, deletedPipeDiameters, onChange]);

      if (error) {
         return <DataLoadingAndErrors error={error} isLoading={false} />;
      }

      return (
         <>
            <DataLoadingAndErrors error={error} isLoading={loading} />
            <ItemTable titleKey={'operator.pipeDiameter.label'} columns={columns} items={pipeDiameters || []}
               onSelect={handleSelect}
               selectId={pipeDiameterId}
               showSearchTextField={false}>
               <Button color='primary' onClick={handleAdd} style={{ marginLeft: 8 }}>
                  <FHGTypography noWrap id={'operator.addPipeDiameter.button'} />
               </Button>
               <Button color='primary' onClick={handleEdit} style={{ marginLeft: 8 }} disabled={!pipeDiameterId}>
                  <FHGTypography noWrap id={'operator.editPipeDiameter.button'} disabled={!pipeDiameterId} />
               </Button>
               <Button color='primary' onClick={handleDelete} style={{ marginLeft: 8 }} disabled={!pipeDiameterId}>
                  <FHGTypography noWrap id={'operator.deletePipeDiameter.button'} disabled={!pipeDiameterId} />
               </Button>
            </ItemTable>
            {showEdit && (
               <PipeDiameterEdit
                  pipeDiameterValue={pipeDiameterValue}
                  onSubmit={onEditCompleted}
                  onClose={() => {
                     setShowEdit(false);
                  }}
               />
            )}
         </>
      );
   }
);

export default PipeDiameterTable;
